import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gavel, Plus, Play, Ban, StopCircle, Wallet } from "lucide-react";
import api from "@/lib/api";
import { Section, DeleteBtn } from "./common";
import { TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = ["Batsman", "Bowler", "All-rounder", "Wicketkeeper"];
const INCREMENTS = [1000, 2500, 5000, 10000];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function StatusChip({ status }) {
  const map = {
    current: "bg-league-blue text-white animate-pulse",
    sold: "bg-green-600 text-white",
    unsold: "bg-league-ink/10 text-league-ink/60",
    upcoming: "bg-league-mist text-league-ink border border-black/10",
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${map[status] || map.upcoming}`}>{status}</span>;
}

function PurseRow({ team, onSaved }) {
  const [val, setVal] = useState(team.purse_total || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auction/purse", { team: team.team, purse_total: +val || 0 });
      toast.success(`${team.team} purse set to ${inr(+val || 0)}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <li className="py-3 flex items-center justify-between gap-3" data-testid={`admin-purse-${team.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-league-ink flex items-center gap-2"><Wallet size={14} className="text-league-blue" /> {team.team}</p>
        {team.purse_total > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">spent {inr(team.spent)} • <span className="text-league-blue font-semibold">{inr(team.purse_left)} left</span></p>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <Input type="number" min="0" step="5000" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Budget ₹" className="bg-white w-32" data-testid={`admin-purse-input-${team.team.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
        <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`admin-purse-save-${team.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}>Set</Button>
      </div>
    </li>
  );
}

export default function AuctionTab() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inc, setInc] = useState("2500");
  const [pForm, setPForm] = useState({ name: "", role: "All-rounder", base_price: 1000, image_url: "" });

  const load = async (silent = false) => {
    try {
      const [p, s, t] = await Promise.all([
        api.get("/auction/players"),
        api.get("/auction/state"),
        api.get("/auction/teams"),
      ]);
      setPlayers(p.data);
      setState(s.data);
      setTeams(t.data);
    } catch {
      if (!silent) toast.error("Failed to load auction data");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const addPlayer = async (e) => {
    e.preventDefault();
    const formEl = e.target;
    let imageUrl = pForm.image_url.trim();
    const file = formEl.photo.files[0];
    if (file) {
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd);
        imageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      } catch (err) {
        setBusy(false);
        toast.error(err.response?.data?.detail || "Photo upload failed");
        return;
      }
      setBusy(false);
    }
    run(async () => {
      await api.post("/auction/players", { name: pForm.name, role: pForm.role, base_price: +pForm.base_price || 1000, image_url: imageUrl || null });
      setPForm({ name: "", role: "All-rounder", base_price: 1000, image_url: "" });
      formEl.reset();
    }, "Player added to the pool");
  };

  const cur = state?.current_player;
  const nextAmount = state?.current_bidder ? state.current_bid + Number(inc) : state?.current_bid;
  const nextBid = (team) => run(() => api.post("/auction/bid", { team: team.team, amount: nextAmount }), `${team.team} bid ${inr(nextAmount)}`);

  return (
    <TabsContent value="auction" data-testid="admin-auction-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Section title="Add a player to the pool">
            <form onSubmit={addPlayer} className="space-y-4" data-testid="admin-auction-player-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ap-name">Player name</Label>
                  <Input id="ap-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required className="bg-white" data-testid="admin-auction-player-name" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={pForm.role} onValueChange={(v) => setPForm({ ...pForm, role: v })}>
                    <SelectTrigger className="bg-white" data-testid="admin-auction-player-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`admin-auction-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ap-base">Base price (₹)</Label>
                  <Input id="ap-base" type="number" min="0" step="500" value={pForm.base_price} onChange={(e) => setPForm({ ...pForm, base_price: e.target.value })} required className="bg-white" data-testid="admin-auction-player-base" />
                </div>
                <div>
                  <Label htmlFor="ap-url">Photo URL (optional)</Label>
                  <Input id="ap-url" value={pForm.image_url} onChange={(e) => setPForm({ ...pForm, image_url: e.target.value })} placeholder="https://…" className="bg-white" data-testid="admin-auction-player-image" />
                </div>
              </div>
              <div>
                <Label htmlFor="ap-photo">Or upload a photo</Label>
                <input
                  id="ap-photo"
                  name="photo"
                  type="file"
                  accept="image/*"
                  data-testid="admin-auction-player-photo"
                  className="mt-1.5 w-full text-sm text-league-ink file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:bg-league-blue file:text-white file:font-semibold file:text-xs file:uppercase file:tracking-wider hover:file:bg-league-deep file:cursor-pointer"
                />
              </div>
              <Button type="submit" disabled={busy} data-testid="admin-auction-player-submit" className="gap-2"><Plus size={15} /> Add player</Button>
            </form>
          </Section>

          <Section title="Team purses">
            <p className="text-xs text-muted-foreground mb-2">Set each franchise's budget — bids that exceed a team's remaining purse get blocked automatically.</p>
            <ul className="divide-y divide-black/5" data-testid="admin-purse-list">
              {teams.map((t) => <PurseRow key={t.team} team={t} onSaved={() => load(true)} />)}
            </ul>
          </Section>

          <Section title={`Player pool (${players.length})`}>
            <ul className="divide-y divide-black/5" data-testid="admin-auction-players-list">
              {players.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-auction-player-${p.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={p.image_url || "/asl-logo.png"} alt="" className="w-10 h-10 rounded-full object-cover border border-black/10 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-league-ink flex items-center gap-2 flex-wrap">
                        {p.name} <StatusChip status={p.status} />
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {p.role} • Base {inr(p.base_price)}
                        {p.status === "sold" && ` • Sold to ${p.sold_to} for ${inr(p.sold_price)}`}
                      </p>
                    </div>
                  </div>
                  <span className="flex shrink-0 items-center">
                    {(p.status === "upcoming" || p.status === "unsold") && !cur && (
                      <button
                        onClick={() => run(() => api.post("/auction/start", { player_id: p.id }), `${p.name} is on the block`)}
                        disabled={busy}
                        data-testid={`admin-auction-start-${p.id}`}
                        className="p-2 rounded-md text-league-blue transition-colors duration-200 hover:bg-league-blue hover:text-white"
                        aria-label="Put on the block"
                        title="Put on the block"
                      >
                        <Play size={15} />
                      </button>
                    )}
                    <DeleteBtn testid={`admin-auction-delete-${p.id}`} onClick={() => run(() => api.delete(`/auction/players/${p.id}`), "Player removed")} />
                  </span>
                </li>
              ))}
              {players.length === 0 && <li className="py-4 text-sm text-muted-foreground">No players yet — add the pool before auction night.</li>}
            </ul>
          </Section>
        </div>

        <Section title="Hammer controls">
          {cur ? (
            <div data-testid="admin-hammer-controls">
              <div className="bg-league-ink text-white rounded-lg p-5 mb-5 flex items-center gap-4">
                <img src={cur.image_url || "/asl-logo.png"} alt={cur.name} className="w-16 h-16 rounded-full object-cover border-2 border-white/20 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-league-red mb-1">On the block</p>
                  <p className="font-oswald text-2xl font-semibold truncate" data-testid="admin-hammer-player">{cur.name}</p>
                  <p className="text-white/60 text-xs mt-0.5">{cur.role} • Base {inr(cur.base_price)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">Bid</p>
                  <p className="font-oswald text-3xl font-semibold" data-testid="admin-hammer-bid">{inr(state.current_bid)}</p>
                  <p className="text-xs text-white/70 font-semibold" data-testid="admin-hammer-bidder">{state.current_bidder || "No bids"}</p>
                </div>
              </div>

              <Label>Increment per click</Label>
              <Select value={inc} onValueChange={setInc}>
                <SelectTrigger className="bg-white mb-4" data-testid="admin-auction-increment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCREMENTS.map((v) => <SelectItem key={v} value={String(v)} data-testid={`admin-auction-inc-${v}`}>+{inr(v)}</SelectItem>)}
                </SelectContent>
              </Select>

              <Label>Raise the paddle for</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5 mb-5" data-testid="admin-auction-teams">
                {teams.map((t) => {
                  const over = t.purse_total > 0 && nextAmount > t.purse_left;
                  return (
                    <button
                      key={t.team}
                      onClick={() => nextBid(t)}
                      disabled={busy || over}
                      data-testid={`admin-bid-${t.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="px-4 py-3 rounded-lg border-2 border-league-blue text-league-blue text-sm font-bold transition-colors duration-150 hover:bg-league-blue hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t.team}
                      {t.purse_total > 0 && (
                        <span className="block text-[10px] font-semibold opacity-70 mt-0.5">{over ? "purse too low" : `${inr(t.purse_left)} left`}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <Button onClick={() => run(() => api.post("/auction/sold"), `SOLD to ${state.current_bidder} for ${inr(state.current_bid)}`)} disabled={busy || !state.current_bidder} data-testid="admin-auction-sold" className="gap-2 bg-green-600 hover:bg-green-700">
                  <Gavel size={15} /> SOLD
                </Button>
                <Button variant="outline" onClick={() => run(() => api.post("/auction/unsold"), "Marked unsold")} disabled={busy} data-testid="admin-auction-unsold" className="gap-2">
                  <Ban size={15} /> Unsold
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-2" data-testid="admin-hammer-idle">
              <p>No player on the block right now.</p>
              <p className="mt-1">Hit the play button next to a player in the pool to open bidding — the auction goes live on the public page automatically.</p>
            </div>
          )}

          {(state?.log || []).length > 0 && (
            <div className="mt-6" data-testid="admin-auction-log">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Bid log</p>
              <ul className="divide-y divide-black/5">
                {[...state.log].reverse().slice(0, 10).map((l, i) => (
                  <li key={i} className="py-2 flex justify-between text-sm">
                    <span className="font-semibold text-league-ink">{l.team}</span>
                    <span className="font-oswald text-league-blue font-semibold">{inr(l.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {state?.live && (
            <Button variant="outline" onClick={() => run(() => api.post("/auction/stop"), "Auction night ended")} disabled={busy} data-testid="admin-auction-stop" className="gap-2 mt-6 text-league-red border-league-red/30">
              <StopCircle size={15} /> End auction night
            </Button>
          )}
        </Section>
      </div>
    </TabsContent>
  );
}
